import React, { useState, useRef, useEffect } from "react";
import { longDateTime } from "../../utils/DateHelper";
import SpectrumChart from "../Charts/SpectrumChart";
import SimpleDataGrid from "../Tables/SimpleDataGrid";
import { CreateSpectrumPoints } from "../../utils/CreatePoints";
import { Trans, useTranslation } from "react-i18next";
import LazyImage from "../Shared/LazyImage";
import BaseModal from "./BaseModal";
import ImageModal from "./imageModal";
import { toCamelCase } from "../../utils/Helpers";
import { MODEL_PROCESSING_TYPES } from "../../utils/constants";

const MeasurementsDetailsModal = ({
    isVisible,
    setIsVisible,
    data,
    fields,
    showSpectralData,
    entityName,
}) => {
    const [showImage, setShowImage] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const isImageOpened = useRef(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [spectrumPoints, setSpectrumPoints] = useState([]);
    const [deletedPoints, setDeletedPoints] = useState([]);
    const { t } = useTranslation();

    useEffect(() => {
        if (data && data.spectralData && showSpectralData) {
            setSpectrumPoints(
                CreateSpectrumPoints({
                    rows: Object.values(data.spectralData),
                    headers: ["wvn", ...data.headers],
                })
            );
        }
    }, [data]);

    const headers = [t("parameterName"), t("predictedValue")];
    const tabs = [t("data"), t("spectrum")];

    const openImage = () => {
        setShowImage(true);
        isImageOpened.current = true;
    };

    const renderTabs = () => {
        return (
            tabs &&
            tabs.map((tab, index) => {
                return (
                    <div
                        key={index}
                        onClick={() => {
                            setActiveTab(index);
                        }}
                        className={`tabs-bnt-item ${
                            activeTab === index ? "active" : ""
                        }`}
                        data-tabs="data-content-wrapper"
                    >
                        {tab}
                    </div>
                );
            })
        );
    };

    const renderFields = () => {
        return (
            fields &&
            fields.map((field, index) => {
                return (
                    <div key={index} className="input-group m-0">
                        <label htmlFor={`input-${index}`} className="uppercase">
                            {field.label}
                        </label>
                        <input
                            type={field.type}
                            id={`input-${index}-${field.name}`}
                            className="input-element"
                            name={field.name}
                            value={
                                field.type === "datetime"
                                    ? longDateTime(data[field.name])
                                    : data[field.name] || "--"
                            }
                            readOnly
                        />
                    </div>
                );
            })
        );
    };

    const renderHeader = () => {
        return (
            headers &&
            headers.map((header, index) => {
                return (
                    <td key={index}>
                        <div className="font-bold px-6 py-3 text-center text-xs text-gray-500 uppercase tracking-wider">
                            <div className="flex items-center justify-center">
                                <span className="ml-2">{header}</span>
                            </div>
                        </div>
                    </td>
                );
            })
        );
    };

    const getPredictedValue = (param) => {
        const value = param?.value ? param.value : t("invalid");
        return param?.type === MODEL_PROCESSING_TYPES.Classification?.value
            ? `${param.label} (${value})`
            : value;
    };

    const renderPredictedValues = () => {
        return (
            data.predictions &&
            Object.keys(data.predictions).map((key, index) => {
                return (
                    <tr key={index}>
                        <td>
                            <div className="px-6 py-3 whitespace-wrap">
                                {key}
                            </div>
                        </td>
                        <td>
                            <div className="px-6 py-3 whitespace-wrap">
                                {getPredictedValue(data.predictions[key])}
                            </div>
                        </td>
                    </tr>
                );
            })
        );
    };
    return (
        <>
            <BaseModal
                isVisible={isVisible}
                setIsVisible={setIsVisible}
                title={`${t(toCamelCase(entityName))} ${t("details")}`}
                titleIcon="fas fa-info-circle"
            >
                <div className="card-table shadow img-uploader flex items-center">
                    <div onClick={openImage}>
                        <LazyImage
                            src={
                                data.image ||
                                require("../../assets/images/Group 709.png")
                                    .default
                            }
                            className="uploaded-img modal-btn-open"
                            data-modal-window="uploaded-img-box"
                        />
                    </div>
                    <div className="img-info ml-5">
                        <div className="sample-name">
                            <Trans i18nKey="sampleName">Sample Name</Trans>
                        </div>
                        <div className="img-uploaded">{data.name}</div>
                    </div>
                </div>
                <div className="main-customer-form-inputs grid-2-cols">
                    {renderFields()}
                </div>
                {data.predictions && Object.keys(data.predictions).length > 0 && (
                    <div className="prediction-table mt-4">
                        <div className="input-group m-0">
                            <label htmlFor="scan-input-1" className="uppercase">
                                <Trans i18nKey="prediction">Prediction</Trans>
                            </label>
                        </div>
                        <div className="table-responsive shadow overflow-hidden border-b border-gray-200 sm:rounded-lg small-scroll-x small-scroll-y">
                            <table
                                className="table-striped min-w-full divide-y divide-gray-200 text-xs text-center w-full"
                                style={{ maxHeight: "350px" }}
                            >
                                <thead>
                                    <tr>{renderHeader()}</tr>
                                </thead>
                                <tbody>{renderPredictedValues()}</tbody>
                            </table>
                        </div>
                    </div>
                )}
                {showSpectralData && (
                    <div className="mt-2 mb-5">
                        <div className="tabs-wrapper">
                            <div className="tabs-btn">{renderTabs()}</div>
                            <div className="tabs-content">
                                <SimpleDataGrid
                                    visible={activeTab === 0}
                                    data={data}
                                    selectedRows={selectedRows}
                                    setSelectedRows={setSelectedRows}
                                />

                                <SpectrumChart
                                    visible={activeTab === 1}
                                    points={spectrumPoints}
                                    selectedRows={selectedRows}
                                    setSelectedRows={setSelectedRows}
                                    deletedPoints={deletedPoints}
                                    setDeletedPoints={setDeletedPoints}
                                    renderSwitch={false}
                                />
                            </div>
                        </div>
                    </div>
                )}
                <div className="input-group mt-3">
                    <label htmlFor="note" className="uppercase">
                        <Trans i18nKey="note">Note</Trans>
                    </label>
                    <textarea
                        value={data.note || "---"}
                        id="note"
                        className="input-element textarea-element"
                        readOnly
                    ></textarea>
                </div>

                {/* IMAGE MODAL */}
                <ImageModal
                    image={data.image}
                    showImage={showImage}
                    closeImage={setShowImage}
                />
            </BaseModal>
        </>
    );
};

export default MeasurementsDetailsModal;
